import {
  newVaultElement,
  withoutAzureKeyVault,
} from "@p0security/integrations-vaulted-credential-shared/components";
import {
  Element,
  InstallSpec,
  ItemComponent,
} from "@p0security/shared/install/types";

import { ConnectorHostingWithServiceAccount } from "./connector-hosting.ts";
import { INVALID_INSTANCE_URL, isInstanceUrl } from "./instance-url.ts";
import { INVALID_ITEM_ID, isItemId } from "./item-id.ts";
import { INVALID_SECRET_NAME, isSecretName } from "./secret-name.ts";

/** Two items, so that one connector can serve several Harbor instances and be
 * reinstalled without touching them.
 *
 * `connector` is one deployment of P0's Harbor connector: where it runs and,
 * from that, the identity it runs as. `harbor-instance` is one Harbor
 * instance: which connector reaches it, and where the connector finds the
 * instance's admin credential: the OIDC credential of a Harbor system
 * administrator, which the connector signs in to Harbor with.
 */
export const HarborComponents = InstallSpec({
  connector: ItemComponent({
    label: "Harbor connector",
    description:
      "Runs P0's Harbor connector in your AWS account or Google Cloud project",
    type: "string",
    maturity: "beta",
    // A connector's request holds only `hosting`, so the install framework
    // gives nothing away when it repeats the request beside a refused id.
    validator: async (id) => (isItemId(id) ? undefined : INVALID_ITEM_ID),
    schema: {
      // The variant that names the connector's identity after the connector,
      // so each instance can grant that identity read on its secret.
      hosting: ConnectorHostingWithServiceAccount,
    },
  }),
  "harbor-instance": ItemComponent({
    label: "Harbor instance",
    description:
      "A Harbor instance P0 can grant project roles, and system administrator, on",
    type: "string",
    maturity: "beta",
    // No item validator, though an instance's id has the connector's rule
    // (`isItemId`). The install framework answers a failing item validator
    // with the whole request, and logs it, and an instance's request may hold
    // the admin credential pasted in place of its secret's name.
    // So the instance's assembler has to check the id itself.
    //
    // Every field is `step: "new"`, so a verify or a configure that changes
    // one is refused. A PUT isn't held to that: the instance's assembler has
    // to refuse a PUT over an existing item. An instance's read grant names
    // its connector's identity and its secret, so a new connector, vault or
    // secret takes a new item. So does a new URL: an open request holds a
    // user's ID on the instance it was made for, and on another instance that
    // ID is nobody's, or someone else's.
    schema: {
      // A `connector` item's id.
      connector: Element({
        label: "Connector",
        description:
          "The Harbor connector that reaches this instance. It must run in the same cloud as the instance's secret.",
        type: "dynamic",
        step: "new",
      }),
      url: Element({
        label: "Harbor URL",
        description:
          "The address your Harbor instance is served at, without a path",
        type: "string",
        placeholder: "https://harbor.example.com",
        step: "new",
        validator: async (_id, url) =>
          isInstanceUrl(url) ? undefined : INVALID_INSTANCE_URL,
      }),
      vault: withoutAzureKeyVault(
        newVaultElement({ owner: "Harbor instance" })
      ),
      secretName: Element({
        label: "Admin credential secret",
        description:
          'The name of the secret that holds the OIDC credential of a Harbor admin account, in the vault above: JSON with "issuer", "clientId", "clientSecret" and "refreshToken". Harbor has to sign users in with OIDC. P0 never reads it; only your connector does.',
        type: "string",
        step: "new",
        validator: async (_id, name) =>
          isSecretName(name) ? undefined : INVALID_SECRET_NAME,
      }),
    },
  }),
});
